import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { PlusOutlined } from "@ant-design/icons";
import { Upload, Tooltip } from "antd";
import APIKit from "api/api/axiosSetup";
import { getFileType, readAsDataURL, validateFile } from "./validation-rules";
import MediaPreview from "./media-preview";
import "./style.css";

const initialState = {
  open: false,
  type: null,
  originalFile: null,
  file: null,
  dimensions: {
    width: null,
    height: null,
    originalWidth: null,
    originalHeight: null,
  },
};

const renderItem = ({ originNode, file, tooltipText }) => {
  return (
    <Tooltip
      placement="top"
      color={"cyan"}
      title={
        file?.isCached
          ? "This file is currently stored in a temporary URL and is not yet linked to any item"
          : tooltipText || null
      }
    >
      <div
        className="relative h-full"
        style={
          file?.isCached
            ? {
                background: "#FFF3CD",
              }
            : file?.error?.message
            ? {
                border: "1px solid #FFEBEB",
              }
            : {
                border: "1px solid #eef0f4",
              }
        }
      >
        {originNode}
        {file?.error?.message && (
          <small className="text-red-500">{file?.error?.message}</small>
        )}
      </div>
    </Tooltip>
  );
};

const ImageVideoPicker = ({
  onChange = () => {},
  as = "image",
  value = null,
  accept = "image/png, image/jpeg, image/jpg",
  maxAllowedFiles = 1,
  validationRules = [],
  label,
  info,
  tooltipText,
  ...restProps
}) => {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState({
    open: false,
    type: "image",
    originalFile: null,
    file: null,
  });
  const [fileList, setFileList] = useState([]);

  // --- Load initial value
  useEffect(() => {
    if (value) {
      if (Array.isArray(value)) {
        const valueList = value?.map((item, index) => {
          if (typeof item === "string") {
            return {
              uid: index,
              name: "media",
              status: "done",
              url: item,
            };
          }
          return item;
        });
        setFileList(valueList);
      } else if (typeof value === "string") {
        setFileList([
          {
            uid: "-1",
            name: "media",
            status: "done",
            url: value,
          },
        ]);
      } else if (value instanceof File || value?.originFileObj) {
        setFileList([
          {
            uid: "-1",
            name: value.name || "media",
            status: "done",
            ...value,
          },
        ]);
      } else if (value?.url) {
        setFileList([
          {
            uid: "-1",
            name: "media",
            status: "done",
            url: value.url,
          },
        ]);
      }
    } else {
      setFileList([]);
    }
  }, [value]);

  // --- Preview modal
  const onPreview = async (file) => {
    let url = await readAsDataURL(file);
    const type = getFileType(file);
    if (url) {
      setModalState({
        open: true,
        type,
        file: url,
        originalFile: file,
        dimensions: {
          width: file?.error?.data?.width,
          height: file?.error?.data?.height,
          originalWidth: file?.error?.data?.originalWidth,
          originalHeight: file?.error?.data?.originalHeight,
        },
      });
    }
  };

  const onRemove = async (file) => {
    const files = fileList.filter((f) => f.uid !== file.uid);
    // setFileList(files);
    onChange(files);
  };

  const handleChange = async ({ file, fileList: incomingList }) => {
    // Validate only the latest changed file, keep previous errors
    const updatedList = await Promise.all(
      incomingList?.map(async (f) => {
        // Skip already validated files
        if (f?.validationChecked) return f;
        if (f?.status === "removed") return f;

        // Validate blobs only
        const origin = f.originFileObj || f;
        const result = await validateFile(origin, validationRules[as], t);
        if (!result.ok) {
          return {
            ...f,
            status: "error",
            error: { message: result?.message, data: result?.data },
            response: { message: result?.message },
            validationChecked: true,
          };
        }
        return { ...f, validationChecked: true };
      })
    );

    // setFileList(updatedList);
    onChange?.(updatedList);
  };

  const labelNode =
    label == null || typeof label === "string"
      ? t(label || "Upload Media")
      : label;

  return (
    <div className="w-full">
      <label className="text-14px flex flex-col gap-0.5">
        {labelNode}
        <span className="text-12px text-gray-500">
          {t(
            info?.text || "default info",
            info?.props || {
              maxSizeMB: 1,
              dimensions: "458x257",
              maxSizeMBVideo: 10,
            }
          )}
        </span>
      </label>

      <Upload
        multiple={maxAllowedFiles > 1}
        accept={accept}
        style={{ width: "100%", height: "100%", padding: 0 }}
        name="media"
        fileList={fileList}
        listType={maxAllowedFiles > 1 ? "picture-card" : "picture"}
        {...restProps}
        beforeUpload={() => false}
        onChange={handleChange}
        onRemove={onRemove}
        onPreview={onPreview}
        itemRender={(originNode, file) =>
          renderItem({
            originNode,
            file,
            tooltipText,
          })
        }
      >
        {fileList?.length < maxAllowedFiles ? (
          <div className="mt-1 flex flex-col gap-2 items-center justify-center border border-dashed w-full border-gray-300 rounded-md p-4">
            <PlusOutlined />
            <div>{t("Upload")}</div>
          </div>
        ) : null}
      </Upload>
      {/* TODO: if you required to use crop, uncomment this code */}
      <MediaPreview
        {...modalState}
        onClose={() => setModalState({ open: false, type: null, file: null })}
      />
    </div>
  );
};

export default ImageVideoPicker;

ImageVideoPicker.propTypes = {
  onChange: PropTypes.func,
  as: PropTypes.oneOf(["image", "video"]),
  value: PropTypes.any,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  info: PropTypes.shape({
    text: PropTypes.string,
    props: PropTypes.object,
  }),
  accept: PropTypes.string,
  maxAllowedFiles: PropTypes.number,
  // Validation rules: merged with defaults inside validator
  validationRules: PropTypes.objectOf(
    PropTypes.shape({
      type: PropTypes.oneOf(["image", "video"]),
      maxSizeMB: PropTypes.number,
      allowedTypes: PropTypes.arrayOf(PropTypes.string),
      dimensions: PropTypes.shape({
        width: PropTypes.number,
        height: PropTypes.number,
      }),
      extensions: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};
